import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Headphones, Flag, MessageSquare, RefreshCw, AlertCircle, Ticket, ShieldAlert, CheckCircle2, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export function CustomerSupportDashboard() {
  const { hasRole, user } = useAuth();
  const isLead = hasRole('customer_support_lead');

  const { data, isLoading } = useQuery({
    queryKey: ['cs-stats', user?.id],
    queryFn: async () => {
      const since = new Date(Date.now() - 30 * 86400_000).toISOString();
      const [
        { count: openReports },
        { count: openDisputes },
        { count: msgs30d },
        { count: openTickets },
        { count: myTickets },
        { count: escalated },
        { count: resolved30d },
      ] = await Promise.all([
        supabase.from('reports' as any).select('*', { count: 'exact', head: true }).eq('status', 'open'),
        supabase.from('lead_charges' as any).select('*', { count: 'exact', head: true }).eq('status', 'disputed'),
        supabase.from('messages' as any).select('*', { count: 'exact', head: true }).gte('created_at', since),
        supabase.from('support_tickets' as any).select('*', { count: 'exact', head: true }).in('status', ['open','pending','in_progress']),
        supabase.from('support_tickets' as any).select('*', { count: 'exact', head: true }).eq('assigned_to', user?.id || '').in('status', ['open','pending','in_progress']),
        supabase.from('support_tickets' as any).select('*', { count: 'exact', head: true }).eq('status', 'escalated'),
        supabase.from('support_tickets' as any).select('*', { count: 'exact', head: true }).eq('status', 'resolved').gte('resolved_at', since),
      ]);
      return {
        openReports: openReports || 0,
        openDisputes: openDisputes || 0,
        msgs30d: msgs30d || 0,
        openTickets: openTickets || 0,
        myTickets: myTickets || 0,
        escalated: escalated || 0,
        resolved30d: resolved30d || 0,
      };
    },
  });

  if (isLoading) return <Skeleton className="h-96" />;

  const stats = [
    { label: 'Open tickets', value: data?.openTickets, icon: Ticket, color: 'text-blue-600' },
    { label: 'My tickets', value: data?.myTickets, icon: Headphones, color: 'text-indigo-600' },
    { label: 'Escalated', value: data?.escalated, icon: ShieldAlert, color: 'text-red-600' },
    { label: 'Resolved (30d)', value: data?.resolved30d, icon: CheckCircle2, color: 'text-emerald-600' },
    { label: 'Open reports', value: data?.openReports, icon: Flag, color: 'text-red-500' },
    { label: 'Lead disputes', value: data?.openDisputes, icon: AlertCircle, color: 'text-amber-500' },
    { label: 'Messages (30d)', value: data?.msgs30d, icon: MessageSquare, color: 'text-blue-500' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Headphones className="h-6 w-6 text-primary" />
          Customer Support {isLead ? 'Lead' : 'Agent'}
        </h1>
        <p className="text-sm text-muted-foreground">Handle complaints, refunds, dispute escalations, and platform support tickets.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {stats.map(s => (
          <Card key={s.label}><CardContent className="p-4 flex items-center gap-3">
            <s.icon className={`h-7 w-7 ${s.color}`} />
            <div><p className="text-xl font-bold leading-none">{s.value}</p><p className="text-[11px] text-muted-foreground mt-1">{s.label}</p></div>
          </CardContent></Card>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle>Quick Actions</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Button asChild className="justify-start"><Link to="/dashboard/support"><Ticket className="h-4 w-4 mr-2" /> Support Ticket Queue</Link></Button>
          <Button asChild variant="outline" className="justify-start"><Link to="/dashboard/reports"><Flag className="h-4 w-4 mr-2" /> Reports & Abuse Queue</Link></Button>
          <Button asChild variant="outline" className="justify-start"><Link to="/dashboard/lead-marketplace"><RefreshCw className="h-4 w-4 mr-2" /> Lead Disputes</Link></Button>
          <Button asChild variant="outline" className="justify-start"><Link to="/dashboard/messages"><MessageSquare className="h-4 w-4 mr-2" /> Messages</Link></Button>
        </CardContent>
      </Card>
    </div>
  );
}
